import { useBoolean } from '@sravni/react-utils';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { checkIsShowAccordionBlockEditIcon } from 'shared/lib';
import { useAppSelector } from 'shared/lib/redux';
import type { IFormHeader } from 'shared/types/IFormHeader';
import { AnketaFormPopup } from 'shared/ui/AnketaFormPopup';
import { CustomAccordionItem } from 'shared/ui/CustomAccordion';

import { selectWorkplaceInfo } from 'src/client/entities/creditForm';

import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';
import { WorkplaceInfoModalTexts } from '../../WorkplaceInfo.texts';
import { Form } from '../Form/Form';

export type TWorkplaceInfoAccordionProps = TAccordionProps & {
  shouldRestoreAdditionalData?: boolean;
};

export const AccordionModalForm: FC<TWorkplaceInfoAccordionProps> = ({
  isOpen,
  shouldForceOpenPopup = false,
  isFormForceOpened = false,
  onFormSubmit,
  isLoading,
  shouldRestoreAdditionalData,
  isDisableEdit = false,
  shouldScrollIntoAccordion,
  isPopupClosable = false,
  onPopupClose,
  children,
  isSummary,
  stepIndex,
  isHideAccordionData,
  formId,
  icon,
  submitButtonText,
  isHideDivider = false,
  accordionIconSize,
  TitleComponent,
  DescriptionComponent,
  defaultDescription,
  defaultTitle,
  isAlwaysShowDescription = false,
  onPopupOpen,
  isAnyFormStepOpenedInPopup,
  analyticsOverrides,
}) => {
  const { data, isFullFilled } = useAppSelector(selectWorkplaceInfo);
  const [shouldShowEditPopup, setShouldShowEditPopup] = useBoolean();
  const [_, setHeader] = useState<IFormHeader>();
  const [title, description] = useAccordionHeaderParams(
    data,
    {
      title: defaultTitle,
      description: defaultDescription,
    },
    isOpen,
    isHideAccordionData,
  );

  const isWithEditItem = checkIsShowAccordionBlockEditIcon(
    isOpen,
    isSummary || isFullFilled,
    isDisableEdit,
    shouldForceOpenPopup,
  );

  const isAccordionOpen = !isFullFilled && isOpen;
  const isEditPopupVisible = shouldShowEditPopup || (shouldForceOpenPopup && isFormForceOpened);
  const isDisabled = !isOpen && !isFullFilled && (shouldForceOpenPopup || isDisableEdit);
  const descriptionText = useMemo(
    () => (!isDisabled || isAlwaysShowDescription ? description : ''),
    [description, isAlwaysShowDescription, isDisabled],
  );
  const isWithDivider = !isSummary && !isHideDivider;

  const onEditButtonClick = useCallback(() => {
    onPopupOpen?.();
    setShouldShowEditPopup.on();
  }, [onPopupOpen, setShouldShowEditPopup]);

  return (
    <>
      <CustomAccordionItem
        onEditButtonClick={onEditButtonClick}
        icon={icon}
        iconSize={accordionIconSize}
        isOpen={isAccordionOpen}
        disabled={isDisabled}
        withEdit={isWithEditItem}
        title={TitleComponent ? <TitleComponent disabled={isDisabled}>{title}</TitleComponent> : title}
        withDivider={isWithDivider}
        description={
          DescriptionComponent ? (
            <DescriptionComponent disabled={isDisabled}>{descriptionText}</DescriptionComponent>
          ) : (
            descriptionText
          )
        }
        scrollIntoView={isAccordionOpen && shouldScrollIntoAccordion}
      >
        <Form
          onFormSubmit={onFormSubmit}
          setHeader={setHeader}
          isFormForceOpened={isFormForceOpened}
          isLoading={isLoading}
          shouldRestoreAdditionalData={shouldRestoreAdditionalData}
          stepIndex={stepIndex}
          formId={formId}
          submitButtonText={submitButtonText}
          isAnyFormStepOpenedInPopup={isAnyFormStepOpenedInPopup}
          analyticsOverrides={analyticsOverrides}
        />
      </CustomAccordionItem>

      <AnketaFormPopup
        submitButtonText={submitButtonText}
        isClosable={isPopupClosable}
        isFormForceOpened
        FormElement={Form}
        onFormSubmit={onFormSubmit}
        onClose={(isSubmit) => {
          setShouldShowEditPopup.off();
          onPopupClose?.(isSubmit);
        }}
        isLoading={isLoading}
        isOpen={isEditPopupVisible}
        icon={icon}
        iconSize={accordionIconSize}
        title={TitleComponent ? <TitleComponent>{defaultTitle}</TitleComponent> : defaultTitle}
        subtitle={
          DescriptionComponent ? (
            <DescriptionComponent>{WorkplaceInfoModalTexts.description}</DescriptionComponent>
          ) : (
            WorkplaceInfoModalTexts.description
          )
        }
        setHeader={setHeader}
        stepIndex={stepIndex}
        analyticsOverrides={analyticsOverrides}
      >
        {children}
      </AnketaFormPopup>
    </>
  );
};
